/**
 * Pluggable company tax ID helpers (CNPJ / NIF / ES_TAX_ID).
 *
 * Prefer these helpers over `if (country === 'PT')` or hardcoding CNPJ rules.
 * `taxIdType` comes from the tenant locale profile (see locale-profile.js).
 *
 * ES_TAX_ID covers DNI/NIF, NIE and entity NIF (legacy “CIF”) with AEAT
 * check digits. Never run CNPJ cleaners on ES.
 */

import { t } from "../i18n/index.js";
import { TAX_ID_TYPE_ES_TAX_ID as ES_TAX_ID_CONST } from "./country-capabilities.js";
import { getCompanyLocaleProfile } from "./locale-profile.js";

export const TAX_ID_TYPE_CNPJ = "CNPJ";
export const TAX_ID_TYPE_NIF = "NIF";
export const TAX_ID_TYPE_ES_TAX_ID = ES_TAX_ID_CONST;

const KNOWN_TAX_ID_TYPES = new Set([TAX_ID_TYPE_CNPJ, TAX_ID_TYPE_NIF, TAX_ID_TYPE_ES_TAX_ID]);

const ES_DNI_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";
const ES_ENTITY_CONTROL_LETTERS = "JABCDEFGHI";
const ES_ENTITY_PREFIXES = new Set("ABCDEFGHJNPQRSUVW".split(""));
const ES_ENTITY_CONTROL_LETTER_ONLY = new Set("NPQRSW".split(""));
const ES_ENTITY_CONTROL_DIGIT_ONLY = new Set("ABEH".split(""));
const ES_ENTITY_CONTROL_EITHER = new Set("CDFGJUV".split(""));
const ES_NIE_PREFIX_MAP = { X: "0", Y: "1", Z: "2" };
const ES_UNEXPECTED_CHARS = /[^A-Za-z0-9\s-]/;
const ES_SEPARATORS = /[\s-]+/g;

function digitsOnly(raw) {
  return String(raw || "").replace(/\D/g, "");
}

/**
 * Strict ES canonicalization: spaces/hyphens only; else reject.
 * @param {unknown} raw
 * @returns {string|null}
 */
function canonicalEsTaxId(raw) {
  const text = String(raw || "").trim();
  if (!text) return null;
  if (ES_UNEXPECTED_CHARS.test(text)) return null;
  const cleaned = text.replace(ES_SEPARATORS, "").toUpperCase();
  if (cleaned.length !== 9 || !/^[A-Z0-9]{9}$/.test(cleaned)) return null;
  return cleaned;
}

/**
 * @param {unknown} raw
 * @returns {"dni"|"nie"|"entity"|null}
 */
export function classifyEsTaxId(raw) {
  const cleaned = canonicalEsTaxId(raw);
  if (!cleaned) return null;
  if (/^\d{8}[A-Z]$/.test(cleaned)) return "dni";
  if (/^[XYZ]\d{7}[A-Z]$/.test(cleaned)) return "nie";
  if (
    ES_ENTITY_PREFIXES.has(cleaned[0]) &&
    /^\d{7}$/.test(cleaned.slice(1, 8)) &&
    /^[A-Z0-9]$/.test(cleaned[8])
  ) {
    return "entity";
  }
  return null;
}

function esDniControlLetter(eightDigits) {
  return ES_DNI_LETTERS[Number(eightDigits) % 23];
}

function validateEsDni(cleaned) {
  const body = cleaned.slice(0, 8);
  if (!/^\d{8}$/.test(body)) return false;
  return cleaned[8] === esDniControlLetter(body);
}

function validateEsNie(cleaned) {
  const mapped = ES_NIE_PREFIX_MAP[cleaned[0]];
  if (!mapped || !/^\d{7}$/.test(cleaned.slice(1, 8))) return false;
  return validateEsDni(mapped + cleaned.slice(1));
}

function esEntityCheckValues(body7) {
  let oddSum = 0;
  let evenSum = 0;
  for (let i = 0; i < body7.length; i += 1) {
    let digit = Number(body7[i]);
    if (i % 2 === 0) {
      digit *= 2;
      oddSum += digit < 10 ? digit : digit - 9;
    } else {
      evenSum += digit;
    }
  }
  const checkDigit = (10 - ((oddSum + evenSum) % 10)) % 10;
  return { digit: String(checkDigit), letter: ES_ENTITY_CONTROL_LETTERS[checkDigit] };
}

function validateEsEntity(cleaned) {
  const org = cleaned[0];
  const body = cleaned.slice(1, 8);
  const control = cleaned[8];
  if (!ES_ENTITY_PREFIXES.has(org) || !/^\d{7}$/.test(body)) return false;
  const { digit, letter } = esEntityCheckValues(body);
  if (ES_ENTITY_CONTROL_LETTER_ONLY.has(org)) return control === letter;
  if (ES_ENTITY_CONTROL_DIGIT_ONLY.has(org)) return control === digit;
  if (ES_ENTITY_CONTROL_EITHER.has(org)) return control === digit || control === letter;
  return false;
}

function cleanEsTaxId(raw) {
  const cleaned = canonicalEsTaxId(raw);
  if (!cleaned || classifyEsTaxId(cleaned) === null) return null;
  return cleaned;
}

function validateEsTaxId(raw) {
  const cleaned = canonicalEsTaxId(raw);
  if (!cleaned) return false;
  const category = classifyEsTaxId(cleaned);
  if (category === "dni") return validateEsDni(cleaned);
  if (category === "nie") return validateEsNie(cleaned);
  if (category === "entity") return validateEsEntity(cleaned);
  return false;
}

/**
 * @param {unknown} [profileOrCompany]
 * @returns {"CNPJ"|"NIF"|"ES_TAX_ID"}
 */
export function resolveTaxIdType(profileOrCompany) {
  const profile = getCompanyLocaleProfile(profileOrCompany);
  const type = String(profile?.taxIdType || TAX_ID_TYPE_CNPJ)
    .trim()
    .toUpperCase();
  return KNOWN_TAX_ID_TYPES.has(type) ? type : TAX_ID_TYPE_CNPJ;
}

/**
 * Resolve tax id type from cached company configuration (localStorage).
 * @returns {"CNPJ"|"NIF"|"ES_TAX_ID"}
 */
export function getTenantTaxIdType() {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return TAX_ID_TYPE_CNPJ;
  }
  try {
    const companyId =
      localStorage.getItem(window.CONFIG?.STORAGE_KEYS?.COMPANY_ID || "auris_company_id") || "";
    const baseKey =
      window.CONFIG?.STORAGE_KEYS?.COMPANY_CONFIGURATION || "auris_company_configuration";
    const scopedKey = companyId ? `${baseKey}:${companyId}` : baseKey;
    const raw = localStorage.getItem(scopedKey) || localStorage.getItem(baseKey) || "";
    if (!raw) return TAX_ID_TYPE_CNPJ;
    const config = JSON.parse(raw);
    return resolveTaxIdType(config);
  } catch {
    return TAX_ID_TYPE_CNPJ;
  }
}

/**
 * @param {unknown} raw
 * @param {"CNPJ"|"NIF"|"ES_TAX_ID"|string} taxIdType
 * @returns {string|null}
 */
export function cleanTaxId(raw, taxIdType) {
  const resolved = String(taxIdType || TAX_ID_TYPE_CNPJ)
    .trim()
    .toUpperCase();
  if (resolved === TAX_ID_TYPE_ES_TAX_ID) {
    return cleanEsTaxId(raw);
  }
  const digits = digitsOnly(raw);
  if (resolved === TAX_ID_TYPE_NIF) {
    return digits.length === 9 ? digits : null;
  }
  return digits.length === 14 ? digits : null;
}

function validateCnpjDigits(digits) {
  if (digits.length !== 14) return false;
  if (digits === digits[0].repeat(14)) return false;

  const calc = (partial, weights) => {
    const total = partial.split("").reduce((sum, digit, i) => sum + Number(digit) * weights[i], 0);
    const remainder = total % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const first = calc(digits.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  if (Number(digits[12]) !== first) return false;
  const second = calc(digits.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return Number(digits[13]) === second;
}

function validateNifDigits(digits) {
  if (digits.length !== 9) return false;
  if (digits === digits[0].repeat(9)) return false;
  let total = 0;
  for (let i = 0; i < 8; i += 1) {
    total += Number(digits[i]) * (9 - i);
  }
  const remainder = total % 11;
  const check = remainder < 2 ? 0 : 11 - remainder;
  return Number(digits[8]) === check;
}

/**
 * @param {unknown} raw
 * @param {"CNPJ"|"NIF"|"ES_TAX_ID"|string} taxIdType
 * @returns {boolean}
 */
export function validateTaxId(raw, taxIdType) {
  const resolved = String(taxIdType || TAX_ID_TYPE_CNPJ)
    .trim()
    .toUpperCase();
  if (resolved === TAX_ID_TYPE_ES_TAX_ID) {
    return validateEsTaxId(raw);
  }
  const digits = digitsOnly(raw);
  if (resolved === TAX_ID_TYPE_NIF) {
    return validateNifDigits(digits);
  }
  return validateCnpjDigits(digits);
}

/**
 * @param {unknown} raw
 * @param {"CNPJ"|"NIF"|"ES_TAX_ID"|string} taxIdType
 * @returns {string}
 */
export function formatTaxId(raw, taxIdType) {
  const resolved = String(taxIdType || TAX_ID_TYPE_CNPJ)
    .trim()
    .toUpperCase();

  if (resolved === TAX_ID_TYPE_ES_TAX_ID) {
    const text = String(raw || "").trim();
    if (ES_UNEXPECTED_CHARS.test(text)) return text.toUpperCase();
    return text.replace(ES_SEPARATORS, "").toUpperCase().slice(0, 9);
  }

  let digits = digitsOnly(raw);

  if (resolved === TAX_ID_TYPE_NIF) {
    digits = digits.slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }

  digits = digits.slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  }
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

/**
 * @param {"CNPJ"|"NIF"|"ES_TAX_ID"|string} [taxIdType]
 * @returns {string}
 */
export function getTaxIdLabel(taxIdType) {
  const resolved = String(taxIdType || getTenantTaxIdType())
    .trim()
    .toUpperCase();
  if (resolved === TAX_ID_TYPE_NIF) return t("fields.taxId.nif");
  if (resolved === TAX_ID_TYPE_ES_TAX_ID) return t("fields.taxId.es");
  return t("fields.taxId.cnpj");
}

/**
 * @param {"CNPJ"|"NIF"|"ES_TAX_ID"|string} [taxIdType]
 * @returns {string}
 */
export function getTaxIdPlaceholder(taxIdType) {
  const resolved = String(taxIdType || getTenantTaxIdType())
    .trim()
    .toUpperCase();
  if (resolved === TAX_ID_TYPE_NIF) return "000 000 000";
  if (resolved === TAX_ID_TYPE_ES_TAX_ID) return "B12345674";
  return "00.000.000/0000-00";
}

/**
 * @param {"CNPJ"|"NIF"|"ES_TAX_ID"|string} [taxIdType]
 * @returns {number}
 */
export function getTaxIdMaxLength(taxIdType) {
  const resolved = String(taxIdType || getTenantTaxIdType())
    .trim()
    .toUpperCase();
  if (resolved === TAX_ID_TYPE_NIF) return 11;
  // Canonical 9 + up to 2 separators while typing.
  if (resolved === TAX_ID_TYPE_ES_TAX_ID) return 11;
  return 18;
}

/**
 * @param {"CNPJ"|"NIF"|"ES_TAX_ID"|string} [taxIdType]
 * @returns {"numeric"|"text"}
 */
export function getTaxIdInputMode(taxIdType) {
  const resolved = String(taxIdType || getTenantTaxIdType())
    .trim()
    .toUpperCase();
  return resolved === TAX_ID_TYPE_ES_TAX_ID ? "text" : "numeric";
}

/**
 * @param {"CNPJ"|"NIF"|"ES_TAX_ID"|string} [taxIdType]
 * @returns {boolean}
 */
export function taxIdUsesLegacyCnpjField(taxIdType) {
  const resolved = String(taxIdType || getTenantTaxIdType())
    .trim()
    .toUpperCase();
  return resolved === TAX_ID_TYPE_CNPJ;
}
